import * as THREE from 'three';
import { Scene3D } from '../3D/Scene3D';
import { SceneObject } from '../types';
import { hasBehavior, getBehavior } from './behaviors';

export interface GameState {
  hasPlayer: boolean;
  collected: number;
  totalCollectibles: number;
  score: number;
  lives: number;
  // 0 when lives aren't in play (a game with no hazards can't cost a life).
  maxLives: number;
  // Seconds remaining, or null when the game has no time limit.
  timeLeft: number | null;
  won: boolean;
  lost: boolean;
  lostReason: 'lives' | 'time' | null;
}

// Drives a minimal but complete game loop over a loaded Scene3D:
// a Player moves on the ground plane, Collectibles are picked up,
// Obstacles block movement, Hazards reset the player, and reaching the
// Goal (after collecting everything, by default) wins.
export class PlayRuntime {
  private scene: Scene3D;
  private onState: (s: GameState) => void;

  private keys: Record<string, boolean> = {};
  private touchDir = { x: 0, z: 0 };
  private playerUuid: string | null = null;
  private playerStart = new THREE.Vector3();
  private speed = 6;

  private collectibles = new Set<string>();
  private points = new Map<string, number>();
  private obstacles: string[] = [];
  private hazards: string[] = [];
  private goalUuid: string | null = null;
  private requireAll = true;

  private total = 0;
  private collected = 0;
  private score = 0;
  private maxLives = 0;
  private lives = 0;
  private timeLimit = 0;
  private timeLeft: number | null = null;
  private lastSecond = -1;
  private won = false;
  private lost = false;
  private lostReason: 'lives' | 'time' | null = null;

  constructor(
    scene: Scene3D,
    objects: SceneObject[],
    onState: (s: GameState) => void
  ) {
    this.scene = scene;
    this.onState = onState;

    for (const o of objects) {
      if (hasBehavior(o, 'player') && !this.playerUuid) {
        this.playerUuid = o.uuid;
        const params = getBehavior(o, 'player')?.params ?? {};
        const sp = params.speed;
        if (typeof sp === 'number') this.speed = sp;
        const lv = params.lives;
        if (typeof lv === 'number' && lv > 0) this.maxLives = lv;
        this.playerStart.set(o.position.x, o.position.y, o.position.z);
      }
      if (hasBehavior(o, 'collectible')) {
        this.collectibles.add(o.uuid);
        const pts = getBehavior(o, 'collectible')?.params?.points;
        this.points.set(o.uuid, typeof pts === 'number' ? pts : 1);
      }
      if (hasBehavior(o, 'obstacle')) this.obstacles.push(o.uuid);
      if (hasBehavior(o, 'hazard')) this.hazards.push(o.uuid);
      if (hasBehavior(o, 'goal') && !this.goalUuid) {
        this.goalUuid = o.uuid;
        const gp = getBehavior(o, 'goal')?.params ?? {};
        this.requireAll = gp.requireAllCollectibles !== false;
        const tl = gp.timeLimit;
        if (typeof tl === 'number' && tl > 0) this.timeLimit = tl;
      }
    }
    this.total = this.collectibles.size;
    // Lives only come into play if there's something that can cost one.
    if (this.hazards.length === 0) this.maxLives = 0;
    else if (this.maxLives === 0) this.maxLives = 3;
    this.lives = this.maxLives;
    this.timeLeft = this.timeLimit > 0 ? this.timeLimit : null;
    this.lastSecond = this.timeLeft !== null ? Math.ceil(this.timeLeft) : -1;
  }

  start() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    this.scene.onFrame = (dt) => this.update(dt);
    this.emit();
  }

  // Called by on-screen touch controls; x/z in roughly -1..1.
  setTouchDir(x: number, z: number) {
    this.touchDir.x = x;
    this.touchDir.z = z;
  }

  dispose() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    this.scene.onFrame = null;
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    const k = e.key.toLowerCase();
    this.keys[k] = true;
    if (k.startsWith('arrow') || k === ' ') e.preventDefault();
  };

  private handleKeyUp = (e: KeyboardEvent) => {
    this.keys[e.key.toLowerCase()] = false;
  };

  private emit() {
    this.onState({
      hasPlayer: !!this.playerUuid,
      collected: this.collected,
      totalCollectibles: this.total,
      score: this.score,
      lives: this.lives,
      maxLives: this.maxLives,
      timeLeft: this.timeLeft,
      won: this.won,
      lost: this.lost,
      lostReason: this.lostReason,
    });
  }

  private radiusOf(obj: THREE.Object3D): number {
    const s = obj.scale;
    return 0.5 * Math.max(s.x, s.y, s.z, 0.5);
  }

  private planarDistance(a: THREE.Object3D, b: THREE.Object3D): number {
    return Math.hypot(a.position.x - b.position.x, a.position.z - b.position.z);
  }

  private update(dt: number) {
    if (this.won || this.lost || !this.playerUuid) return;
    const player = this.scene.objects.get(this.playerUuid);
    if (!player) return;

    // --- Countdown timer ---
    if (this.timeLeft !== null) {
      this.timeLeft = Math.max(0, this.timeLeft - dt);
      const sec = Math.ceil(this.timeLeft);
      if (sec !== this.lastSecond) {
        this.lastSecond = sec;
        this.emit(); // refresh the HUD about once a second, not every frame
      }
      if (this.timeLeft <= 0) {
        this.lost = true;
        this.lostReason = 'time';
        this.emit();
        return;
      }
    }

    // --- Movement input (XZ plane) ---
    let dx = 0;
    let dz = 0;
    if (this.keys['arrowup'] || this.keys['w']) dz -= 1;
    if (this.keys['arrowdown'] || this.keys['s']) dz += 1;
    if (this.keys['arrowleft'] || this.keys['a']) dx -= 1;
    if (this.keys['arrowright'] || this.keys['d']) dx += 1;
    dx += this.touchDir.x;
    dz += this.touchDir.z;

    if (dx !== 0 || dz !== 0) {
      const len = Math.hypot(dx, dz);
      dx /= len;
      dz /= len;
      const step = this.speed * Math.min(dt, 0.05);
      const prevX = player.position.x;
      const prevZ = player.position.z;
      player.position.x += dx * step;
      player.position.z += dz * step;

      // Obstacles block movement — revert if we'd overlap one.
      const pr = this.radiusOf(player);
      for (const id of this.obstacles) {
        const o = this.scene.objects.get(id);
        if (!o) continue;
        if (this.planarDistance(player, o) < pr + this.radiusOf(o)) {
          player.position.x = prevX;
          player.position.z = prevZ;
          break;
        }
      }
    }

    const pr = this.radiusOf(player);

    // --- Collectibles ---
    for (const id of Array.from(this.collectibles)) {
      const o = this.scene.objects.get(id);
      if (!o) {
        this.collectibles.delete(id);
        continue;
      }
      if (this.planarDistance(player, o) < pr + this.radiusOf(o)) {
        this.scene.removeObject(id);
        this.collectibles.delete(id);
        this.collected += 1;
        this.score += this.points.get(id) ?? 1;
        this.emit();
      }
    }

    // --- Hazards ---
    for (const id of this.hazards) {
      const o = this.scene.objects.get(id);
      if (!o) continue;
      if (this.planarDistance(player, o) < pr + this.radiusOf(o)) {
        player.position.copy(this.playerStart);
        if (this.maxLives > 0) {
          this.lives -= 1;
          if (this.lives <= 0) {
            this.lives = 0;
            this.lost = true;
            this.lostReason = 'lives';
          }
          this.emit();
        }
        break;
      }
    }
    if (this.lost) return;

    // --- Win condition ---
    if (this.goalUuid) {
      const g = this.scene.objects.get(this.goalUuid);
      if (g) {
        const ready = !this.requireAll || this.collectibles.size === 0;
        if (ready && this.planarDistance(player, g) < pr + this.radiusOf(g)) {
          this.won = true;
          this.emit();
        }
      }
    } else if (this.total > 0 && this.collectibles.size === 0) {
      // No goal object, but there were collectibles — collecting them all wins.
      this.won = true;
      this.emit();
    }
  }
}
